import React, {useState} from 'react';
import {Delete, Edit} from '@mui/icons-material';
import {IconButton} from '@mui/material';
import {Box, Button, AppBar, Toolbar, Grid, Typography, Modal, LinearProgress, TextField} from '@mui/material';
import {
    Add
} from '@mui/icons-material';
import ConfirmEmailMessage from "./ConfirmEmailMessage";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {CSS} from '@dnd-kit/utilities';
import {stopPropagation} from "@dnd-kit/core/dist/sensors/events";

import {Slide} from '../services/types'

interface ChapterSlide {
    id: string;
    slide: Slide;
}

interface Chapter {
    id: string;
    name: string;
    project_id: string;
    sort: number;
    external_id?: string;
    slides: ChapterSlide[];
}

interface ChapterBoxProps {
    chapter: Chapter;
    onAddSlide: (chapterId: string) => void;
    onAddAllSlide: (chapterId: string) => void;
    onReorderSlides: (chapterId: string, newOrder: string[]) => void;
    onDeleteSlide: (chapterSlideId: string) => void;
    onEditSlide: (chapterSlide: Slide) => void;
}
interface SortableSlideItemProps {
    slide: ChapterSlide;
    onDelete: (chapterSlideId: string) => void;
    onEdit: (chapterSlide: Slide) => void;
}
const ChapterBox: React.FC<ChapterBoxProps> = ({chapter, onAddSlide, onAddAllSlide, onReorderSlides, onDeleteSlide, onEditSlide}) => {
    const [isDragging, setIsDragging] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );


    const handleDragMove = (event: DragEndEvent) => {
        setIsDragging(true);
    };
    const handleDragEnd = (event: DragEndEvent) => {
        setIsDragging(false);

        const {active, over} = event;
        if (over && active.id !== over.id) {
            const oldIndex = chapter.slides.findIndex((slide) => slide.id === active.id);
            const newIndex = chapter.slides.findIndex((slide) => slide.id === over.id);
            const newOrder = arrayMove(chapter.slides, oldIndex, newIndex);
            onReorderSlides(chapter.id, newOrder.map(slide => slide.id));
        }
    };



    const SortableSlideItem: React.FC<SortableSlideItemProps> = ({ slide, onDelete, onEdit }) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: slide.id });


        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
        };
        const handleDelete = (e: React.MouseEvent) => {
            e.stopPropagation();
            console.log(isDragging)
            if (!isDragging) {
                onDelete(slide.id);
            }
        };
        const handleEdit= (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!isDragging) {
                onEdit(slide.slide);
            }
        };
        return (
            <Box
                ref={setNodeRef}
                style={style}
                {...attributes}
                {...listeners}
                className='chapterSlideItem'
                sx={{
                    position: 'relative',
                    '&:hover .deleteButton': {opacity: 1},
                    '&:hover .editButton': {opacity: 1}
                }}
            >
                <img src={slide.slide.preview} alt={slide.slide.name} />
                <IconButton
                    className="editButton"
                    sx={{
                        position: 'absolute',
                        top: 5,
                        right: 50,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.9)'}
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => {e.stopPropagation(); handleEdit(e);}}
                    // onClick={handleDelete}
                >
                    <Edit/>
                </IconButton>
                <IconButton
                    className="deleteButton"
                    sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.9)'}
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => {e.stopPropagation(); handleDelete(e);}}
                    // onClick={handleDelete}
                >
                    <Delete/>
                </IconButton>
            </Box>
        );
    };

    return (

        <Box mb={4} sx={{pr: '12px'}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                <Typography variant="h6">{chapter.name}</Typography>
                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Button sx={{color: '#3a485b', display: 'flex', gap: '4px'}} onClick={() => onAddSlide(chapter.id)}>
                        <Add/> Добавить слайд
                    </Button>
                    <Button sx={{color: '#3a485b', display: 'flex', gap: '4px'}} onClick={() => onAddAllSlide(chapter.id)}>
                        <Add/> Добавить все слайды
                    </Button>
                </Box>
            </Box>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragMove={handleDragMove}>
                <SortableContext items={chapter.slides.map(slide => slide.id)} strategy={verticalListSortingStrategy}>
                    <Box className='chapterSlideItems'>
                        {chapter.slides.map((slideitem) => (
                            <SortableSlideItem key={slideitem.id} slide={slideitem} onDelete={() => onDeleteSlide(slideitem.id)} onEdit={() => onEditSlide(slideitem.slide)} />
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>
        </Box>
    );
};
export default ChapterBox;